import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, catchError, finalize, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { BaseService } from './base.service';
import { LoadingService } from './loading.service';
import { Order, OrderList } from '../models/domain/Order';
import { BaseSearchOptions } from '../models/core/table/BaseSearchOptions';
import { PagedResponse } from '../models/general/PagedResponse';
import { UpdateOrderStatus } from '../models/domain/UpdateOrderStatus';
import { AcceptOrderDto } from '../models/domain/AcceptOrderDto';

@Injectable({
    providedIn: 'root'
})
export class OrderService extends BaseService<OrderList> {

    constructor(http: HttpClient, loadingService: LoadingService) {
        super(http, `${environment.apiUrl}/v1/order`, loadingService);
    }

    /** List grid — returns lightweight OrderList rows, no items or history */
    public getAllOrders(parameters?: BaseSearchOptions): Observable<HttpResponse<PagedResponse<OrderList>>> {
        if (parameters) {
            this.currentParameters.updateByObject(parameters);
        }
        const params = new HttpParams()
            .set('filter', this.currentParameters.filter)
            .set('orderBy', this.currentParameters.orderBy)
            .set('pageSize', this.currentParameters.pageSize)
            .set('pageIndex', this.currentParameters.pageIndex)
            .set('orderByDirection', this.currentParameters.orderByDirection)
            .set('expand', this.currentParameters.expand);

        this.loadingService.showLoading();
        return this.httpClient
            .get<PagedResponse<OrderList>>(this.baseApiUrl, { params, observe: 'response' })
            .pipe(
                catchError(error => {
                    this.loadingService.hideLoading();
                    return throwError(() => error);
                }),
                finalize(() => this.loadingService.hideLoading()),
            );
    }

    /** Detail popup — returns full Order with items (dieNo, dieName, categoryName) + status history */
    public getOrderById(orderId: number): Observable<HttpResponse<Order>> {
        this.loadingService.showLoading();
        return this.httpClient
            .get<Order>(`${this.baseApiUrl}/${orderId}`, { observe: 'response' })
            .pipe(
                catchError(error => {
                    this.loadingService.hideLoading();
                    return throwError(() => error);
                }),
                finalize(() => this.loadingService.hideLoading()),
            );
    }

    /** Update order status (B2B admin) */
    public updateStatus(dto: UpdateOrderStatus): Observable<HttpResponse<Order>> {
        this.loadingService.showLoading();
        return this.httpClient
            .put<Order>(`${this.baseApiUrl}/status`, dto, { observe: 'response' })
            .pipe(
                catchError(error => {
                    this.loadingService.hideLoading();
                    return throwError(() => error);
                }),
                finalize(() => this.loadingService.hideLoading()),
            );
    }

    /** Accept order with per-item rates (B2B admin) */
    public acceptOrder(dto: AcceptOrderDto): Observable<HttpResponse<Order>> {
        this.loadingService.showLoading();
        return this.httpClient
            .put<Order>(`${this.baseApiUrl}/accept`, dto, { observe: 'response' })
            .pipe(
                catchError(error => {
                    this.loadingService.hideLoading();
                    return throwError(() => error);
                }),
                finalize(() => this.loadingService.hideLoading()),
            );
    }
}
